import { css, keyframes } from '@emotion/react';

import { Box, Group, Modal, OptimizeImage, Stack, Tabs, Text } from '@inflearn/ds-react-next';

import { useQueryClient } from '@tanstack/react-query';
import { random } from 'lodash-es';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NEXT_PUBLIC_CDN_URL } from '../../../appConfig';
import { API_ENDPOINT } from '../../../resources/@types/accountSchema';
import { Confetti } from '../../components/common/Confetti';
import AppShellLayoutForCSR from '../../components/layout/AppShellLayoutForCSR';
import CustomHead from '../../components/layout/CustomHead';
import { type GameType, RankingBoard } from '../../components/pages/games/common';
import { DeveloperNoteButton } from '../../components/pages/games/common/DeveloperNoteButton';
import { GameExitConfirmModal } from '../../components/pages/games/common/GameExitConfirmModal';
import { GameHeader } from '../../components/pages/games/common/GameHeader';
import { GameResultModal } from '../../components/pages/games/common/GameResultModal';
import { GameSelector } from '../../components/pages/games/common/GameSelector';
import { PartyLookFeed } from '../../components/pages/games/common/PartyLookFeed';
import { SlotDressUpResultModal } from '../../components/pages/games/common/SlotDressUpResultModal';
import {
  GAME_QUERY_KEY,
  GAME_QUERY_PARAMS,
  GAME_TYPE_CONFIG,
  GAME_TYPE_KEYS,
  getGameTypeFromQuery,
} from '../../components/pages/games/common/types';
import { useGameExitPrevention } from '../../components/pages/games/hooks/useGameExitPrevention';
import { MoleGame } from '../../components/pages/games/mole-game';
import { RedLightGreenLightGame } from '../../components/pages/games/red-light-green-light';
import { SlotDressUpGame } from '../../components/pages/games/slot-dress-up';
import { BgmPlayer } from '../../components/pages/games/slot-dress-up/BgmPlayer';
import { CDN_BASE_PATH } from '../../components/pages/games/slot-dress-up/itemData';
import { useGamesResultsCreateMutation } from '../../mutations/useGamesResultsCreateMutation';
import { useGamesStatsQuery } from '../../queries/useGamesStatsQuery';

interface GameStates {
  PARTY_LOOK_COLLECTION: {
    isStarted: boolean;
    result: 'win' | 'lose' | null;
    score: number;
    resultId: number | null;
  };
  MOLE_ATTACK: {
    isStarted: boolean;
    result: 'win' | 'lose' | null;
    score: number;
    resultId: number | null;
  };
  GET_OFF_WORK: {
    isStarted: boolean;
    result: 'win' | 'lose' | null;
    score: number;
    resultId: number | null;
  };
  SLOT_DRESS_UP: {
    isStarted: boolean;
    result: 'win' | 'lose' | null;
    score: number;
    resultId: number | null;
  };
}

const getInitialTab = (query: string | string[] | undefined): string => {
  const queryValue = typeof query === 'string' ? query : undefined;
  const gameType = getGameTypeFromQuery(queryValue);
  if (gameType) {
    return GAME_TYPE_CONFIG[gameType].title;
  }
  return GAME_TYPE_CONFIG[GAME_TYPE_KEYS[0]].title;
};

const ForbiddenPage = () => {
  const router = useRouter();
  const [activeTab, setActiveTab] = useState(() => getInitialTab(router.query[GAME_QUERY_KEY]));
  const [pendingTab, setPendingTab] = useState<string | null>(null);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showDeveloperNote, setShowDeveloperNote] = useState(false);

  // 쿼리스트링으로 초기 탭 설정 (클라이언트 사이드 렌더링 시 router.query가 늦게 로드됨)
  useEffect(() => {
    if (router.isReady && router.query[GAME_QUERY_KEY]) {
      const tab = getInitialTab(router.query[GAME_QUERY_KEY]);
      setActiveTab(tab);
    }
  }, [router.isReady, router.query]);

  // 탭에서 GameType 찾기
  const getGameTypeFromTab = useCallback((tab: string): GameType => {
    const entry = Object.entries(GAME_TYPE_CONFIG).find(([, config]) => config.title === tab);
    return entry ? (entry[0] as GameType) : GAME_TYPE_KEYS[0];
  }, []);

  // URL 쿼리 파라미터 업데이트
  const updateQueryParam = useCallback(
    (gameType: GameType) => {
      const queryValue = GAME_QUERY_PARAMS[gameType];
      router.replace(
        {
          pathname: router.pathname,
          query: { [GAME_QUERY_KEY]: queryValue },
        },
        undefined,
        { shallow: true },
      );
    },
    [router],
  );

  // 슬롯 드레스업 전용 모달 상태
  const [showSlotResultModal, setShowSlotResultModal] = useState(false);
  const [slotImageUrl, setSlotImageUrl] = useState<string | null>(null);
  const slotImageUrlRef = useRef<string | null>(null);
  const queryClient = useQueryClient();
  const { mutate: createGameResult } = useGamesResultsCreateMutation({});

  console.log('createGameResult', createGameResult);

  const [gameStates, setGameStates] = useState<GameStates>({
    PARTY_LOOK_COLLECTION: {
      isStarted: false,
      result: null,
      score: 0,
      resultId: null,
    },
    MOLE_ATTACK: {
      isStarted: false,
      result: null,
      score: 0,
      resultId: null,
    },
    GET_OFF_WORK: {
      isStarted: false,
      result: null,
      score: 0,
      resultId: null,
    },
    SLOT_DRESS_UP: {
      isStarted: false,
      result: null,
      score: 0,
      resultId: null,
    },
  });

  // 현재 활성화된 게임 타입 가져오기
  const getCurrentGameType = useCallback((): GameType => {
    return getGameTypeFromTab(activeTab);
  }, [activeTab, getGameTypeFromTab]);

  // 현재 게임이 진행 중인지 확인
  const isAnyGameInProgress = useCallback(() => {
    return (
      gameStates.PARTY_LOOK_COLLECTION.isStarted ||
      gameStates.MOLE_ATTACK.isStarted ||
      gameStates.GET_OFF_WORK.isStarted ||
      gameStates.SLOT_DRESS_UP.isStarted
    );
  }, [gameStates]);

  // 파티룩 모음 탭인지 확인
  const isPartyLookCollectionTab = activeTab === GAME_TYPE_CONFIG.PARTY_LOOK_COLLECTION.title;

  // 탭 변경 핸들러
  const handleTabChange = useCallback(
    (value: string | null) => {
      if (!value) {
        return;
      }

      const currentGameType = getCurrentGameType();
      const isCurrentGameRunning = gameStates[currentGameType].isStarted;

      if (isCurrentGameRunning) {
        setPendingTab(value);
        setShowExitConfirm(true);
      } else {
        setActiveTab(value);
        // URL 쿼리 파라미터 업데이트
        const newGameType = getGameTypeFromTab(value);
        updateQueryParam(newGameType);
      }
    },
    [gameStates, getCurrentGameType, getGameTypeFromTab, updateQueryParam],
  );

  // 탭 변경 확인
  const handleConfirmTabChange = useCallback(() => {
    if (pendingTab) {
      const currentGameType = getCurrentGameType();
      // 현재 게임 상태 초기화
      setGameStates((prev) => ({
        ...prev,
        [currentGameType]: {
          isStarted: false,
          result: null,
          score: 0,
          resultId: null,
        },
      }));
      setActiveTab(pendingTab);
      // URL 쿼리 파라미터 업데이트
      const newGameType = getGameTypeFromTab(pendingTab);
      updateQueryParam(newGameType);
      setPendingTab(null);
    }
    setShowExitConfirm(false);
  }, [pendingTab, getCurrentGameType, getGameTypeFromTab, updateQueryParam]);

  // 탭 변경 취소
  const handleCancelTabChange = useCallback(() => {
    setPendingTab(null);
    setShowExitConfirm(false);
  }, []);

  // 게임 시작 핸들러
  const handleGameStart = useCallback((gameType: GameType) => {
    setGameStates((prev) => ({
      ...prev,
      [gameType]: {
        isStarted: true,
        result: null,
        score: 0,
        resultId: null,
      },
    }));
    setShowResultModal(false);
  }, []);

  // 두더지 게임 종료 핸들러
  const handleMoleGameOver = useCallback(
    (score: number) => {
      // 중복 호출 방지: 이미 resultId가 있으면 무시
      if (gameStates.MOLE_ATTACK.resultId !== null) {
        return;
      }

      // 게임 결과 저장
      createGameResult(
        {
          score,
          gameType: 'MOLE_ATTACK',
          comment: '',
        },
        {
          onSuccess: (data) => {
            if (data?.data?.resultId) {
              setGameStates((prev) => ({
                ...prev,
                MOLE_ATTACK: {
                  isStarted: false,
                  result: null,
                  score,
                  resultId: Number(data?.data?.resultId),
                },
              }));
              setShowResultModal(true);
              return;
            }

            alert('게임 결과 저장에 실패했습니다.');
          },
          onError: () => {
            alert('게임 결과 저장에 실패했습니다.');
          },
        },
      );
    },
    [createGameResult, gameStates.MOLE_ATTACK.resultId],
  );

  // 무궁화 게임 종료 핸들러
  const handleRedLightGameOver = useCallback(
    (result: 'win' | 'lose', score: number) => {
      // 중복 호출 방지: 이미 resultId가 있거나 결과가 설정되어 있으면 무시
      if (gameStates.GET_OFF_WORK.resultId !== null || gameStates.GET_OFF_WORK.result !== null) {
        return;
      }

      // 게임 성공 시에만 결과 저장
      if (result === 'win') {
        createGameResult(
          {
            score,
            gameType: 'GET_OFF_WORK',
            comment: '',
          },
          {
            onSuccess: (data) => {
              if (data?.data?.resultId) {
                setGameStates((prev) => ({
                  ...prev,
                  GET_OFF_WORK: {
                    isStarted: false,
                    result,
                    score,
                    resultId: Number(data?.data?.resultId),
                  },
                }));
                setShowResultModal(true);
                return;
              }

              alert('게임 결과 저장에 실패했습니다.');
            },
            onError: () => {
              alert('게임 결과 저장에 실패했습니다.');
            },
          },
        );
      } else {
        // 실패 시에는 resultId 없이 상태만 업데이트하고 모달 표시
        setGameStates((prev) => ({
          ...prev,
          GET_OFF_WORK: {
            isStarted: false,
            result,
            score,
            resultId: null,
          },
        }));
        setShowResultModal(true);
      }
    },
    [createGameResult, gameStates.GET_OFF_WORK.resultId, gameStates.GET_OFF_WORK.result],
  );

  // 슬롯 드레스업 게임 종료 핸들러 (아웃트로 화면 표시만, 결과는 프로필 설정 시 저장)
  const handleSlotDressUpGameOver = useCallback(() => {
    // 게임 완료 상태로 변경 (결과 저장은 프로필 설정 시)
    setGameStates((prev) => ({
      ...prev,
      SLOT_DRESS_UP: {
        isStarted: false,
        result: 'win',
        score: 0,
        resultId: null,
      },
    }));
  }, []);

  // 슬롯 드레스업 이미지 저장 핸들러 (프로필 설정 시 CDN URL과 함께 결과 저장)
  const handleSlotDressUpImageSaved = useCallback(
    (cdnUrl: string) => {
      // 이미 결과가 저장되어 있으면 무시
      if (gameStates.SLOT_DRESS_UP.resultId !== null) {
        return;
      }

      // 이미지 URL 저장 (ref와 state 모두 업데이트)
      slotImageUrlRef.current = cdnUrl;
      setSlotImageUrl(cdnUrl);

      // 게임 결과 저장 (CDN URL 포함)
      createGameResult(
        {
          score: 0,
          gameType: 'SLOT_DRESS_UP',
          comment: '',
          imageUrl: cdnUrl,
        },
        {
          onSuccess: (data) => {
            if (data?.data?.resultId) {
              // CDN URL이 확실히 전달되도록 다시 설정 (ref와 state 모두 업데이트)
              slotImageUrlRef.current = cdnUrl;
              setSlotImageUrl(cdnUrl);
              setGameStates((prev) => ({
                ...prev,
                SLOT_DRESS_UP: {
                  ...prev.SLOT_DRESS_UP,
                  resultId: Number(data?.data?.resultId),
                },
              }));
              // 랭킹 목록 새로고침
              queryClient.invalidateQueries({
                queryKey: [API_ENDPOINT.CLIENT_V1_GAMES_STATS],
              });
              // 슬롯 드레스업 전용 모달 열기
              setShowSlotResultModal(true);
              return;
            }

            alert('게임 결과 저장에 실패했습니다.');
          },
          onError: () => {
            alert('게임 결과 저장에 실패했습니다.');
          },
        },
      );
    },
    [createGameResult, gameStates.SLOT_DRESS_UP.resultId, queryClient],
  );

  // 재시작 핸들러
  const handleRestart = useCallback(() => {
    const currentGameType = getCurrentGameType();
    setGameStates((prev) => ({
      ...prev,
      [currentGameType]: {
        isStarted: false,
        result: null,
        score: 0,
        resultId: null,
      },
    }));
    setShowResultModal(false);
    queryClient.invalidateQueries({
      queryKey: [API_ENDPOINT.CLIENT_V1_GAMES_STATS],
    });
  }, [getCurrentGameType, queryClient]);

  // 슬롯 드레스업 모달 닫기 핸들러 (결과화면은 유지, 모달만 닫음)
  const handleSlotDressUpModalClose = useCallback(() => {
    setShowSlotResultModal(false);
  }, []);

  // 슬롯 드레스업 코멘트 제출 후 핸들러
  const handleSlotCommentSubmit = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [API_ENDPOINT.CLIENT_V1_GAMES_STATS],
    });
  }, [queryClient]);

  // 코멘트 제출 후 랭킹 새로고침
  const handleCommentSubmit = useCallback(() => {
    // 코멘트 제출 후 아무 동작 없음 (모달은 내부에서 처리)
  }, []);

  // 페이지 이탈 방지 훅 사용
  useGameExitPrevention({
    enabled: isAnyGameInProgress(),
    message: '게임이 진행 중입니다. 나가시면 게임 기록이 저장되지 않습니다.',
  });

  const currentGameType = getCurrentGameType();
  const currentGameState = gameStates[currentGameType];

  // 파티룩 모음 피드 데이터 가져오기
  const { data: partyLookFeedData } = useGamesStatsQuery({
    gameType: 'SLOT_DRESS_UP',
    period: 'all',
  });

  // 이미지가 있는 피드만 필터링하고, 최신순으로 정렬
  const filteredFeedData = useMemo(() => {
    if (!partyLookFeedData) {
      return [];
    }
    return partyLookFeedData
      .filter((entry) => entry.imageUrl)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [partyLookFeedData]);

  // 피드 데이터를 SET 단위로 나누기 (SET1: 6개 슬롯, SET2: 5개 슬롯 반복)
  // 각 슬롯의 위치는 고정되며, 데이터가 없는 슬롯은 비워둠
  const feedSets = useMemo(() => {
    const sets: { type: 'set1' | 'set2'; items: ((typeof filteredFeedData)[number] | null)[] }[] =
      [];
    let dataIndex = 0;
    let setIndex = 0;

    while (dataIndex < filteredFeedData.length) {
      const isSet1 = setIndex % 2 === 0;
      const slotCount = isSet1 ? 6 : 5;
      const remainingData = filteredFeedData.length - dataIndex;
      const itemsForThisSet = Math.min(slotCount, remainingData);

      // 슬롯 배열 생성 (데이터가 있는 슬롯만 채움, 나머지는 null)
      const slots: ((typeof filteredFeedData)[number] | null)[] = [];
      for (let i = 0; i < itemsForThisSet; i++) {
        slots.push(filteredFeedData[dataIndex + i]);
      }

      sets.push({
        type: isSet1 ? 'set1' : 'set2',
        items: slots,
      });

      dataIndex += itemsForThisSet;
      setIndex++;
    }

    return sets;
  }, [filteredFeedData]);

  // 게임 성공 여부 확인
  const isGameSuccess =
    (showResultModal &&
      ((currentGameType === 'MOLE_ATTACK' && currentGameState.score > 0) ||
        (currentGameType === 'GET_OFF_WORK' && currentGameState.result === 'win'))) ||
    (showSlotResultModal && currentGameType === 'SLOT_DRESS_UP');

  return (
    <div css={gameContainerStyle}>
      {/* 배경 장식 - 반짝이는 별들 */}
      <BackgroundSparkles />
      <GameSelector value={activeTab} onTabChange={handleTabChange}>
        <Box style={isPartyLookCollectionTab ? gameBoxFullWidthStyle : gameBoxStyle}>
          {/* 개발자의 편지 버튼 - 게임 타이틀 오른쪽 상단 */}
          {!isPartyLookCollectionTab && (
            <DeveloperNoteButton onClick={() => setShowDeveloperNote(true)} />
          )}

          {/* BGM Player - 슬롯 드레스업 탭일 때만 표시 */}
          {activeTab === GAME_TYPE_CONFIG.SLOT_DRESS_UP.title && <BgmPlayer />}

          <Tabs.Panel value={GAME_TYPE_CONFIG.PARTY_LOOK_COLLECTION.title}>
            {/* 배경 이미지 */}
            <Box
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${CDN_BASE_PATH}/ch_ending_bg.png)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                borderRadius: '20px',
              }}
            />
            {/* 콘텐츠 영역 - 가상 스크롤 적용 */}
            <PartyLookFeed feedSets={feedSets} />
            {/* BGM Player - 우측 상단 고정 (스크롤 영역 밖) */}
            <Box
              css={{
                '& > div[role="button"]': {
                  position: 'absolute',
                  top: 20,
                  right: 20,
                  zIndex: 100,
                },
                // ch_img_highlight.png 이미지 숨기기
                '& img[src*="ch_img_highlight"]': {
                  display: 'none !important',
                },
              }}
            >
              <BgmPlayer />
            </Box>
          </Tabs.Panel>
          <Tabs.Panel value={GAME_TYPE_CONFIG.MOLE_ATTACK.title}>
            <GameHeader gameTypeKey="MOLE_ATTACK" />
            <MoleGame
              key={`mole-${gameStates.MOLE_ATTACK.resultId || 'new'}`}
              isGameStarted={gameStates.MOLE_ATTACK.isStarted}
              onGameStart={() => handleGameStart('MOLE_ATTACK')}
              onGameOver={handleMoleGameOver}
            />
          </Tabs.Panel>
          <Tabs.Panel value={GAME_TYPE_CONFIG.GET_OFF_WORK.title}>
            <GameHeader gameTypeKey="GET_OFF_WORK" />
            <RedLightGreenLightGame
              key={`redlight-${gameStates.GET_OFF_WORK.resultId || 'new'}`}
              isGameStarted={gameStates.GET_OFF_WORK.isStarted}
              onGameStart={() => handleGameStart('GET_OFF_WORK')}
              onGameOver={handleRedLightGameOver}
            />
          </Tabs.Panel>
          <Tabs.Panel value={GAME_TYPE_CONFIG.SLOT_DRESS_UP.title}>
            <GameHeader gameTypeKey="SLOT_DRESS_UP" />
            <SlotDressUpGame
              isGameStarted={gameStates.SLOT_DRESS_UP.isStarted}
              onGameStart={() => handleGameStart('SLOT_DRESS_UP')}
              onGameOver={handleSlotDressUpGameOver}
              onImageSaved={handleSlotDressUpImageSaved}
              onRestart={handleRestart}
            />
          </Tabs.Panel>
        </Box>
      </GameSelector>
      {!isPartyLookCollectionTab && <RankingBoard gameType={currentGameType} />}
      <GameResultModal
        isOpen={showResultModal}
        score={currentGameState.score}
        gameType={currentGameType}
        gameResult={currentGameState.result}
        resultId={currentGameState.resultId}
        onRestart={handleRestart}
        onCommentSubmit={handleCommentSubmit}
      />
      <SlotDressUpResultModal
        isOpen={showSlotResultModal}
        imageUrl={slotImageUrlRef.current || slotImageUrl}
        resultId={gameStates.SLOT_DRESS_UP.resultId}
        onClose={handleSlotDressUpModalClose}
        onCommentSubmit={handleSlotCommentSubmit}
      />
      <GameExitConfirmModal
        isOpen={showExitConfirm}
        onConfirm={handleConfirmTabChange}
        onCancel={handleCancelTabChange}
      />
      {isGameSuccess &&
        typeof window !== 'undefined' &&
        createPortal(
          <div style={{ position: 'fixed', inset: 0, zIndex: 99999, pointerEvents: 'none' }}>
            <Confetti
              count={50}
              colors={[
                '#22c55e', // 초록색 (성공)
                '#10b981', // 에메랄드
                '#84cc16', // 라임
                '#eab308', // 노란색
                '#f59e0b', // 오렌지
                '#3b82f6', // 파란색
                '#8b5cf6', // 보라색
                '#ec4899', // 핑크
                '#06b6d4', // 시안
                '#14b8a6', // 틸
                '#f97316', // 주황
                '#ef4444', // 빨강
              ]}
            />
          </div>,
          document.body,
        )}
      <Modal
        opened={showDeveloperNote}
        onClose={() => setShowDeveloperNote(false)}
        title={
          <Group gap={8} align="center">
            <OptimizeImage
              src={`${CDN_BASE_PATH}/btn_letter_hovered_final.png`}
              w={50 * 2}
              alt="letter"
              width={34}
              height={32}
              css={{
                marginTop: '-6px',
                marginLeft: '-7px',
              }}
            />
            <Text>개발자의 편지</Text>
          </Group>
        }
        size="lg"
        centered
        trapFocus={false}
      >
        <Stack gap={16} px={16} pt={16} pb={32}>
          <Text size="sm" c="gray.7">
            만들면서 든 생각…..
            <br />
            <br />
            저는 공부는 기본적으로 힘들고 외로운 행위라는 말에 크게 공감하는 편입니다. 그 힘들고
            외로운 정도를 줄여주는 것이 학습 플랫폼, 인프런의 역할 중 하나이고 그걸 저희가 해왔다고
            생각해요. 예를 들어 자막, 번역, 더빙, 퀴즈, 노트 등은 공부를 덜 힘들게 만들어주는 축에
            속하고, 채팅, 성장로그, 라이브 세션 등은 덜 외롭게 만들어주는 축에 속한다고 생각합니다.
            <br />
            <br />
            이런 기능들을 붙여봐도 여전히 공부는 힘들고 외롭습니다. 게임처럼 재밌는 것으로 만들 수
            있는 방법이 있을까 꽤 오래 고민해 봤지만, 사실 불가능에 가까운 것 같아요. 공부의 본질이
            그런건가 싶기도 하구요.
            <br />
            <br />
            다만 이번에 게임을 만들면서 들었던 생각은,{' '}
            <strong>
              공부 자체를 재미있게 만들 수는 없더라도 공부가 지쳤을때 쉬어갈 수 있고 서로를 응원할
              수 있는 공간을 제공
            </strong>
            할 수 있지 않을까 하는 거였어요. 그런 공간이 있다면 공부하다 지쳤을 때 너무 멀리
            벗어나서 딴짓을 하는 것이 아니라, 적어도 근처에 머무르게 만들고 공부하러 다시 돌아갈
            마음이 들도록 도와줄 수 있을 거예요. 그리고 이런 단순한 환기용 게임과 대시보드, 응원의
            한마디 등이 그 시작점이 될 수 있지 않을까 해요.
            <br />
            <br />
            저는 스팀이라는 게임 플랫폼을 좋아하는데요, 스팀이 잘 된 이유는 게임 공급(할인..)도
            있겠지만 커뮤니티의 역할이 제일 컸다고 생각해요. 세상에는 다양한 게임들이 있고 게임
            하나를 플레이하는 사람들은 그리 많지 않아도, 세상 모든 게임을 다 모아놓은 공간을
            제공하면 게임이라는 공통 관심사를 가진 사람들이 모인 커뮤니티가 되더라구요. 스팀은
            커뮤니티를 통해 다른 사람들이 게임을 어떻게 즐기고 있는지 알 수도 있고, 공략을 얻기도
            하고, 프로필에 전시된 게임이나 아이템을 구경하기도 하고, 취향이 비슷한 게임을 찾을 수도
            있습니다. 또 게임 외의 도전과제, 뱃지나 전시 시스템으로 커뮤니티 자체를 게임화시키고
            수집욕구를 자극해서 딱히 게임을 하지 않더라도 스팀 커뮤니티에 지속적으로 참여할 이유를
            꾸준히 제공합니다. 결국 이런 커뮤니티를 통해서 "게임 시작-끝"이라는 단순한 사이클이
            아니라 "게임 시작-끝-커뮤니티-다른 게임"으로 이어지는 순환 구조가 되는데요, 저는 게임을
            강의로 바꿔서 보더라도 크게 다르지 않다고 생각합니다.
            <br />
            <br />
            강의 하나를 듣는 사람은 그리 많지 않아도, 강의를 듣는 모든 사람들을 모아두면 어떤 종류의
            공부든 상관없이 공부를 하는 사람들의 모임이 됩니다. 사람들이 모이고(커뮤니티), 각자가
            자신만의 공간(프로필, 커리어 기록)을 꾸밀 수 있고, 그 둘을 게임과 같이 쉽게 접근할 수
            있는 매개체를 통해 엮어서 비슷한 구조를 만들어볼 수 있을 거예요. 또 인프런 내의 수많은
            활동들을 자신의 공개된 프로필에 전시할 수 있는 형태(열매, 뱃지, 도전과제, 잔디 등등)로
            바꿔서 제공한다면 딱히 글을 쓰지 않더라도 인프런에서 강의를 듣고 활동하는 행위
            자체만으로 커뮤니티 활성화에 도움을 주게 된다고 생각해요. 깃허브에서 잔디를 열심히 심고
            스타를 많이 받은 누군가의 프로필에 들어가면 자극을 받아서 커밋 하나라도 더 남기게 되는
            것과 동일한 이유로요. 이렇게 만들어진 커뮤니티는 힘들고 외로운 공부의 과정을 견딜 수
            있는 힘이 되어 줄 수 있을거예요.
            <br />
            <br />왜 게임의 형태여야 하느냐 라고 물어보신다면 이런 단순한 구조(짧은 시간, 학습이
            필요없을 정도의 단순성)의 게임들은 머리를 비우게 만들고 게임을 플레이하는 순간에 집중할
            수 있게 되는데요, 게임이 끝나고 점수가 나오면 자연스럽게 순위로 시선이 가고 내 위, 아래
            사람들을 확인하게 됩니다. 이건 어떤 게임이든 항상 비슷했던 것 같아요. 그렇게 시선이
            옮겨간 곳에 다른 사람들이 어떤 강의를 듣다가 이 게임을 하러 왔는지, 또 다시 언제 어떤
            공부를 하러 갔는지, 그리고 다른 사람들이 남기고 간 응원의 한마디 등을 보여준다면
            외로움과 힘듦을 해소해 줄 수 있을 거라고 생각했습니다.
            <br />
            <br />
            추석때 향로 챌린지를 보면서 사람들은 AI 때문에 공부를 안 하는게 아니라, 책상 앞에 앉는게
            힘들기 때문에, 외롭고 힘든 그 과정을 견디는 것이 어려워서 공부를 하지 않고 있던 거라는
            생각이 들었어요. 향로를 매개체로 커뮤니티가 만들어지고, 사람들의 응원과 감시(?) 속에서
            공부를 하고, 언젠가 들어야지 하고 미뤄두었던 강의를 다 듣고, 새로운 공부를 시작하는
            성공적인 순환 구조가 만들어지는걸 볼 수 있었어요. 이 구조가 증명되었으니 더 확장해서,
            누군가 중심이 되어 사람들을 모으지 않더라도 인프런 유저들이 서로에게 응원이 되고 자극을
            주고받을 수 있는 시스템을 만들 수 있기를 바라봅니다.
            <br />
            <br />
            우드(BE), 고슈(FE), 올리(PD) 올림
          </Text>
        </Stack>
      </Modal>
    </div>
  );
};

// 배경 장식 컴포넌트 - 반짝이는 별들
const BackgroundSparkles = () => {
  const pastelColors = [
    '#FFB3BA', // 파스텔 핑크
    '#BAFFC9', // 파스텔 민트
    '#FFDFBA', // 파스텔 피치
    '#BAE1FF', // 파스텔 스카이블루
    '#FFFFBA', // 파스텔 레몬
  ];

  // 랜덤 위치에 별 생성
  const stars = useMemo(
    () =>
      Array.from({ length: 20 }, (_, i) => ({
        id: i,
        top: `${random(0, 100, true)}%`,
        left: `${random(0, 100, true)}%`,
        size: random(4, 12, true), // 4~12px
        color: pastelColors[random(0, pastelColors.length - 1, false)],
        delay: `${random(0, 2, true)}s`,
        duration: random(1.5, 2.5, true), // 1.5~2.5초
      })),
    [],
  );

  return (
    <>
      {stars.map((star) => (
        <Box
          key={star.id}
          pos="absolute"
          top={star.top}
          left={star.left}
          w={star.size}
          h={star.size}
          css={{
            borderRadius: '50%',
            backgroundColor: star.color,
            opacity: 0.6,
            animation: `${twinkleStar} ${star.duration}s ease-in-out infinite`,
            animationDelay: star.delay,
            zIndex: 0,
            boxShadow: `
              0 0 ${star.size * 3}px ${star.color},
              0 0 ${star.size * 5}px ${star.color},
              0 0 ${star.size * 8}px ${star.color},
              0 0 ${star.size * 12}px rgba(255, 255, 255, 0.5)
            `,
            filter: 'blur(0.5px)',
            pointerEvents: 'none',
          }}
        />
      ))}
    </>
  );
};

export default ForbiddenPage;

ForbiddenPage.getLayout = (page: React.ReactNode) => {
  return (
    <>
      <CustomHead
        title={'미니 게임'}
        description={
          '공부하다가 잠깐 쉬고 싶을 때! 인프런 미니게임으로 가볍게 즐기고 소소한 재미를 느껴보세요.'
        }
        image={`${NEXT_PUBLIC_CDN_URL}/public/games/og_xmasGame.png`}
      />
      <AppShellLayoutForCSR
        disableBottomNavigation
        requiredLogin={true}
        showChanneltalk={false}
        showFooter={false}
      >
        {page}
      </AppShellLayoutForCSR>
    </>
  );
};

// 반짝이는 별 애니메이션
const twinkleStar = keyframes`
  0%, 100% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
`;

// Container Styles
const gameContainerStyle = css`
  min-height: calc(100vh - 65px);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  background: 
    radial-gradient(circle at 25% 25%, rgba(255, 179, 186, 0.06) 0%, transparent 50%),
    radial-gradient(circle at 75% 75%, rgba(186, 255, 201, 0.06) 0%, transparent 50%),
    radial-gradient(circle at 50% 10%, rgba(255, 223, 186, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 10% 90%, rgba(186, 225, 255, 0.06) 0%, transparent 50%),
    radial-gradient(circle at 90% 50%, rgba(255, 255, 186, 0.06) 0%, transparent 50%),
    linear-gradient(135deg, #FFE5E8 0%, #E5F5EC 25%, #FFF4E5 50%, #E5F0FF 75%, #FFFEE5 100%),
    linear-gradient(135deg, #f0fdf4 0%, #dcfce7 25%, #bbf7d0 50%, rgb(162, 246, 173) 75%, rgb(151, 245, 165) 100%);
  background-size: 100% 100%, 100% 100%, 100% 100%, 100% 100%, 100% 100%, 100% 100%, 100% 100%;
  padding: 20px;
  position: relative;
  overflow: hidden;
`;

const gameBoxStyle = {
  background: '#ffffff',
  borderRadius: '20px',
  padding: '40px',
  boxShadow: `
    0 25px 50px rgba(34, 197, 94, 0.15),
    0 10px 30px rgba(59, 130, 246, 0.1),
    0 0 0 1px rgba(34, 197, 94, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.6)
  `,
  width: '960px',
  height: '720px',
  display: 'flex',
  flexDirection: 'column' as const,
  overflow: 'visible' as const,
  position: 'relative' as const,
  backdropFilter: 'blur(12px)',
};

// 파티룩 모음 탭용 전체 너비 스타일 (gameBox 960px + gap 20px + RankingBoard 450px)
const gameBoxFullWidthStyle = {
  ...gameBoxStyle,
  width: '1430px',
  padding: 0,
  overflow: 'hidden' as const,
};
